import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Facebook, Instagram, Mail, MapPin, Youtube } from "lucide-react";

import { API, BUILD_VERSION } from "../config/api";

export default function Footer() {
  const [backendVersion, setBackendVersion] = useState("");
  const [backendDb, setBackendDb] = useState("");

  useEffect(() => {
    fetch(`${API}/debug/system-info`)
      .then(res => res.json())
      .then(data => {
        setBackendVersion(data.build_version || "");
        setBackendDb(data.db_name || "");
      })
      .catch(() => {});
  }, []);

  return (
    <footer className="bg-kerala-green text-white" data-testid="footer">
      <div className="section-container">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.jpg"
                alt="Tatvamasi Logo"
                className="w-14 h-14 object-contain bg-white rounded-lg p-1"
              />
              <div>
                <h3 className="font-heading font-semibold text-lg">Tatvamasi</h3>
                <p className="text-sm text-white/60">Kerala Hindu's of London Ontario</p>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Preserving and celebrating the rich cultural heritage of Kerala through 
              community events and traditions in London, Ontario.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-kerala-gold">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { to: "/picnic-2026", label: "Annual Picnic 2026" },
                { to: "/gallery", label: "Gallery" },
                { to: "/sponsors", label: "Become a Sponsor" },
                { to: "/about", label: "About Us" },
                { to: "/contact", label: "Contact" },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to}
                    className="text-white/70 hover:text-kerala-gold transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-kerala-gold">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-kerala-gold mt-0.5" />
                <a href="mailto:tatvamasilondonontario@gmail.com" className="text-white/70 hover:text-white transition-colors">
                  tatvamasilondonontario@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-kerala-gold mt-0.5" />
                <span className="text-white/70">London, Ontario, Canada</span>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-kerala-gold">Follow Us</h4>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/share/17tzecSnkU/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-kerala-gold hover:text-kerala-green rounded-full flex items-center justify-center transition-all"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/tatvamasilondonontario?igsh=NzZ3cXIzdHV1andj" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-kerala-gold hover:text-kerala-green rounded-full flex items-center justify-center transition-all"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@tatvamasilondonontario5371" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-kerala-gold hover:text-kerala-green rounded-full flex items-center justify-center transition-all"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
            <p className="mt-6 text-sm text-white/60">
              Stay connected for updates about our events and community activities.
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p>© 2026 Tatvamasi - Kerala Hindu's of London Ontario.</p>
            <span className="text-white/40 text-xs" data-testid="build-version">
              FE: {BUILD_VERSION}{backendVersion ? ` | BE: ${backendVersion}` : ""}{backendDb ? ` | DB: ${backendDb}` : ""}
            </span>
          </div>
          <div className="flex gap-6">
            <Link to="/contact" className="hover:text-kerala-gold transition-colors">
              Contact
            </Link>
            <Link to="/privacy" className="hover:text-kerala-gold transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
